import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

// SERVICES
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { ToastrService } from 'ngx-toastr';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { ApiService } from 'src/app/services/api.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { PushNotificationService } from '../push-notification.service';
import { ChangeDetectionService } from 'src/app/services/change-detection.service';

// CONSTANTS
import { 
  PUSH_NOTIFICATION_TABLE_HEADERS,
  PUSH_NOTIFICATION_TABLE_SUB_HEADERS 
} from 'src/app/lib/constants/push-notifcation.constants';


@Component({
  selector: 'app-features-push-notif',
  templateUrl: './features-push-notif.component.html',
  styleUrls: ['./features-push-notif.component.scss']
})
export class FeaturesPushNotifComponent implements OnInit {

  // table
  tableHeader = PUSH_NOTIFICATION_TABLE_HEADERS;
  tableSubHeader = PUSH_NOTIFICATION_TABLE_SUB_HEADERS;

  // images
  cover_img: any;
  feature_img: any;
  pushNotif_lists: any;
  isLoading = true;
  
  // table visibility control
  showTable = false;
  isDataLoaded = false;
  isTableLoading = false;
  
  // request state tracking
  isSilentFetchInProgress = false;
  silentFetchFailed = false;

  // change detection
  originalValues: any = {};

  // search
  searchKey: any = null;
  searchForm: UntypedFormGroup;
  search_keyword = '';

  // limit
  limitKey: any = null;
  limitForm: UntypedFormGroup;

  // pagination
  page = 1;
  size = 5;
  total_pages: any;
  current_page = 1;
  pages_array = [];
  total: any;
  lastPage: any;

  public pushNotifForm: UntypedFormGroup;

  // images
  imageFile: {link: string, file: any, name: string};
  imageFile2: {link: string, file: any, name: string};

  // sorting
  sort_column = "createDate";
  sort_type: 'ASC' | 'DESC' = 'DESC';

  currentDate = new Date();
  status: any;

  // print
  tabId: any;
  fileName: any;
  url: any;
  params = {
    search: '',
  }

  constructor(
    private toastr: ToastrService,
    private pushNotificationService: PushNotificationService,
    private popUpDialogService: PopUpDialogService,
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private changeDetectionService: ChangeDetectionService,
    public request: RequestsService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    console.log('initialize push notif');
    // Store initial empty state
    this.storeOriginalValues();
    
    // Silently fetch data in the background
    this.silentFetchData().catch(() => {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
    });
  }

  initializeForm() {
    this.pushNotifForm = this.fb.group({
      title: ['', Validators.required],
      message: ['', Validators.required],
      coverImage: [''],  // Optional
      featureImage: [''], // Optional
    });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      this.get_pushNotifList().catch((error) => {
        this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
      });
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      this.get_pushNotifList().catch((error) => {
        this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
      });
    }, 500);

    this.searchForm = this.fb.group({
      search: ['']
    });

    this.limitForm = this.fb.group({
      limit: ['']
    });
  }

  async silentFetchData() {
    // for checking if the first data loads si done or not preventing multiple requests 
    this.isSilentFetchInProgress = true;
    this.silentFetchFailed = false;

    try {
      const params = {
        search: null,
        sortBy: {
          [this.sort_column]: this.sort_type
        }
      };


      const response = await firstValueFrom(
        this.apiService.getPushNotifications(params, this.current_page, this.size)
      );

      if (response.status === 200) {
        this.status = response.result.status;
        this.pushNotif_lists = response.result.data;
        this.isDataLoaded = true;
        this.isTableLoading = false;
        this.isLoading = false;
        this.silentFetchFailed = false;

        this.pages_array = this.paginationHelper.generatePageArray(
          this.current_page,
          response.result.totalPages
        );

        this.total_pages = response.result.totalPages;
        this.current_page = response.result.currentPage;
        this.total = response.result.totalItems;
        this.lastPage = response.result.totalPages;
        this.toastr.success('Push notifications are ready to view.', 'Loaded');
      }
    } catch (error) {
      this.isDataLoaded = false;
      this.silentFetchFailed = true;
    } finally {
      // Mark that silent fetch is complete
      this.isSilentFetchInProgress = false;
    }
  }

  async showTableData() {
    // Case 1: Data already loaded successfully - just show it
    if (this.isDataLoaded && this.pushNotif_lists && this.pushNotif_lists.length > 0) {
      this.showTable = true;
      this.isLoading = false;
      return;
    }

    // Case 2: Silent fetch is still in progress - do nothing, just wait
    if (this.isSilentFetchInProgress) {
      this.toastr.info('Loading data, please wait...', 'In Progress');
      return;
    }

    // Case 3: Silent fetch failed or no data - make a new request
    if (this.silentFetchFailed || !this.isDataLoaded) {
      this.isTableLoading = true;
      
      await this.get_pushNotifList();
      
      // Check if data was successfully loaded
      if (this.pushNotif_lists && this.pushNotif_lists.length > 0) {
        this.showTable = true;
        this.isLoading = false;
      } else {
        // No data returned, show button again
        this.showTable = false;
        this.toastr.error('Unable to Load Table. Please Try Again Later');
      }
      
      this.isTableLoading = false;
    }
  }

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC') {
    this.isTableLoading = true;

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;

    if (this.pushNotif_lists) {
      this.get_pushNotifList().catch((error) => {
        this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
      });
    }

    console.log('sort_column', this.sort_column);
    console.log('sort_type', this.sort_type);
  }

  onSelectFileCover(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      const link = event.srcElement.files[0];
      const name = event.srcElement.files[0].name;
      reader.onload = (event: any) => {
        this.imageFile = {
          link: event.target.result,
          file: link,
          name
        };
        this.cover_img = event.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    }
    console.log(event.srcElement.files[0].name);
  }

  onSelectFileFeatured(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      const link = event.srcElement.files[0];
      const name = event.srcElement.files[0].name;
      reader.onload = (event: any) => {
        this.imageFile2 = {
          link: event.target.result,
          file: link,
          name
        };
        this.feature_img = event.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  }

  reset() {
    this.pushNotifForm.reset();
    this.cover_img = null;
    this.feature_img = null;
    this.imageFile = null;
    this.imageFile2 = null;
    
    // Reset to original (empty) state after reset
    this.storeOriginalValues();
  }

  async get_pushNotifList() {
    this.isTableLoading = true;
    this.isLoading = true;
    this.isDataLoaded = false;

    const params = {
      search: this.search_keyword === '' ? null : this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type
      }
    };


    this.pages_array = [];

    try {
      const response = await firstValueFrom(
        this.apiService.getPushNotifications(params, this.current_page, this.size)
      );

      if (response.status === 200) {
        this.status = response.result.status;

        this.pushNotif_lists = response.result.data;
        this.isLoading = false;
        this.isDataLoaded = true;

        this.pages_array = this.paginationHelper.generatePageArray(
          this.current_page,
          response.result.totalPages
        );

        this.total_pages = response.result.totalPages;
        this.current_page = response.result.currentPage;
        this.total = response.result.totalItems;
        this.lastPage = response.result.totalPages;
      }

    } finally {
      this.isTableLoading = false;
    }
  }

  async store() {
    const title = this.pushNotifForm.controls.title.value;
    const message = this.pushNotifForm.controls.message.value;

    // Check required fields
    if (!title || title.trim() === '') {
      this.toastr.error('Please enter Title', 'Required Field Missing');
      return;
    }

    if (!message || message.trim() === '') {
      this.toastr.error('Please enter Message', 'Required Field Missing');
      return;
    }

    // Confirm before saving
    this.popUpDialogService.openSaveActionDialog(
      'Are you sure you want to save this push notification?',
      async () => {
        await this.pushNotificationService.performStore({
          title,
          message,
          coverImage: this.imageFile?.file || null,
          featureImage: this.imageFile2?.file || null
        });
        
        this.current_page = this.total_pages;

        // RESET FORM AND IMAGES
        this.pushNotifForm.reset();
        this.cover_img = null;
        this.feature_img = null;
        this.imageFile = null;
        this.imageFile2 = null;
        
        // Store the new empty state as original values
        this.storeOriginalValues();

        await this.get_pushNotifList();
      },
      () => {
        console.log('Save cancelled by user');
      }
    );
  }

  setPage(num) {
    this.current_page = num;
    this.get_pushNotifList().catch((error) => {
      this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
    });
  }

  nextPage() {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.get_pushNotifList().catch((error) => {
        this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
      });
    }
  }

  prevPage() {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_pushNotifList().catch((error) => {
        this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
      });
    }
  }

  keyDownFunction(event) {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList() {
    this.current_page = 1;
    this.search_keyword = this.searchForm.value.search;
    console.log('searchKeyword', this.search_keyword);
    this.get_pushNotifList().catch((error) => {
      this.toastr.error(`Please refresh or contact the developer if the error still occurs: ${error}`, 'Failed Fetching Push Notifications');
    });
  }

  storeOriginalValues() {
    const valuesToStore = {
      title: this.pushNotifForm.controls.title.value || '',
      message: this.pushNotifForm.controls.message.value || '',
      coverImageName: this.imageFile?.name || null,
      featureImageName: this.imageFile2?.name || null
    };

    this.originalValues = this.changeDetectionService.cloneValues(valuesToStore);
  }

  getCurrentValues(): any {
    return {
      title: this.pushNotifForm.controls.title.value || '',
      message: this.pushNotifForm.controls.message.value || '',
      // Compare image file names instead of form control values
      coverImageName: this.imageFile?.name || null,
      featureImageName: this.imageFile2?.name || null
    };
  }

  hasChanges(): boolean {
    const currentValues = this.getCurrentValues();
    const hasFormChanges = this.changeDetectionService.hasChanges(
      this.originalValues, 
      currentValues
    );
    
    return hasFormChanges;
  }

  isSubmitDisabled(): boolean {
    return !this.hasChanges();
  }

  disable(uid: number, value: number) {
    const pushNotif = this.pushNotif_lists.find(p => p.uid === uid);
    const pushNotifTitle = pushNotif?.title || 'this push notification';
        
    this.popUpDialogService.openStatusActionDialog(
      `Are you sure you want to disable "${pushNotifTitle}"?`,
      'DISABLE',
      'CANCEL',
      async () => {
        await this.pushNotificationService.changeStatus(uid, value);
        await this.get_pushNotifList();
      },
      () => {
        console.log('❌ Disable cancelled by user');
      }
    );
  }

  enable(uid: number, value: number) {
    const pushNotif = this.pushNotif_lists.find(p => p.uid === uid);
    const pushNotifTitle = pushNotif?.title || 'this push notification';
        
    this.popUpDialogService.openStatusActionDialog(
      `Are you sure you want to enable "${pushNotifTitle}"?`,
      'ENABLE',
      'CANCEL',
      async () => {
        await this.pushNotificationService.changeStatus(uid, value);
        await this.get_pushNotifList();
      },
      () => {
        console.log('❌ Enable cancelled by user');
      }
    );
  }
}
